import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  TrendingUp,
  Speed,
  Security,
  Analytics,
  AccountBalance,
  Business,
  CloudSync,
  VerifiedUser,
  AutoAwesome,
  RocketLaunch,
  Dashboard,
  SmartToy,
  Payment,
  Inventory,
  ShoppingCart,
  Build,
  TrendingDown,
  Receipt,
  Campaign,
  Assessment,
  Warehouse,
  LocalShipping,
  AdminPanelSettings,
} from '@mui/icons-material';

const HomeScreen: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const features = [
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'Lightning Fast Processing',
      description: 'AI-powered approval in minutes, not weeks. Get funds when you need them most.',
      color: '#2196F3',
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Bank-Grade Security',
      description: 'Enterprise-level encryption and compliance. Your data is always protected.',
      color: '#4CAF50',
    },
    {
      icon: <Analytics sx={{ fontSize: 40 }} />,
      title: 'Smart Analytics',
      description: 'Real-time insights into your business finances. Make data-driven decisions.',
      color: '#9C27B0',
    },
    {
      icon: <CloudSync sx={{ fontSize: 40 }} />,
      title: 'Cloud-First Platform',
      description: 'Access your account anywhere, anytime. Seamless integration with business tools.',
      color: '#FF9800',
    },
    {
      icon: <VerifiedUser sx={{ fontSize: 40 }} />,
      title: 'Transparent Pricing',
      description: 'No hidden fees, no surprises. Clear, upfront rates you can trust.',
      color: '#F44336',
    },
    {
      icon: <SmartToy sx={{ fontSize: 40 }} />,
      title: 'AI-Powered Risk Assessment',
      description: 'Advanced algorithms ensure fair evaluation and faster approvals.',
      color: '#00BCD4',
    },
  ];

  const stats = [
    { value: '24-48', label: 'Hours to Approval', suffix: 'hrs' },
    { value: '1-10', label: 'Loan Range', suffix: ' Lakhs' },
    { value: '100%', label: 'Digital Process', suffix: '' },
    { value: '99.9%', label: 'Uptime', suffix: '' },
  ];

  const useCases = [
    {
      icon: <Payment sx={{ fontSize: 40 }} />,
      title: 'Salary Bridge Loans',
      description: 'Pay your employees on time! Many SMEs wait for customer payments that arrive mid-month, but salaries are due on the 1st. Get instant funds to bridge the gap and maintain employee trust.',
      color: '#FF5722',
      featured: true,
    },
    {
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      title: 'Raw Material Procurement',
      description: 'Never miss a production deadline. Secure funds quickly to purchase raw materials when bulk discounts are available or when urgent orders require immediate material procurement.',
      color: '#4CAF50',
      featured: true,
    },
    {
      icon: <TrendingDown sx={{ fontSize: 40 }} />,
      title: 'Working Capital Management',
      description: 'Smooth out cash flow fluctuations. Access funds to maintain day-to-day operations during slow business periods or while waiting for receivables.',
      color: '#2196F3',
      featured: false,
    },
    {
      icon: <Inventory sx={{ fontSize: 40 }} />,
      title: 'Inventory Management',
      description: 'Stock up before peak seasons. Get funding to build inventory before demand surges, ensuring you never lose sales due to stockouts.',
      color: '#9C27B0',
      featured: false,
    },
    {
      icon: <Build sx={{ fontSize: 40 }} />,
      title: 'Emergency Equipment Repair',
      description: 'Keep operations running smoothly. When critical machinery breaks down, get funds immediately to repair or replace equipment without halting production.',
      color: '#FF9800',
      featured: false,
    },
    {
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      title: 'Vendor Payment Delays',
      description: 'Manage payment cycles effectively. When customers delay payments, get instant funds to pay your vendors on time and maintain supplier relationships.',
      color: '#00BCD4',
      featured: false,
    },
    {
      icon: <Receipt sx={{ fontSize: 40 }} />,
      title: 'Tax & Compliance Payments',
      description: 'Stay compliant without stress. Access funds for GST, TDS, and other tax obligations, ensuring your business maintains good standing with authorities.',
      color: '#F44336',
      featured: false,
    },
    {
      icon: <Campaign sx={{ fontSize: 40 }} />,
      title: 'Marketing & Growth Campaigns',
      description: 'Invest in growth opportunities. Launch marketing campaigns, participate in trade shows, or invest in business expansion when opportunities arise.',
      color: '#E91E63',
      featured: false,
    },
    {
      icon: <Warehouse sx={{ fontSize: 40 }} />,
      title: 'Seasonal Cash Flow Gaps',
      description: 'Navigate seasonal businesses smoothly. Cover expenses during off-seasons while preparing for peak periods, ensuring year-round stability.',
      color: '#795548',
      featured: false,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      }}
    >
      {/* Floating Tech Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          pointerEvents: 'none',
          '& > *': {
            position: 'absolute',
            opacity: 0.1,
            animation: 'float 20s infinite ease-in-out',
          },
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary.main, 0.3)})`,
              left: `${(i * 16) % 100}%`,
              top: `${(i * 20) % 100}%`,
              animationDelay: `${i * 2}s`,
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                '50%': { transform: 'translateY(-50px) rotate(180deg)' },
              },
            }}
          />
        ))}
      </Box>

      {/* App Bar for Admin Login */}
      <AppBar
        position="fixed"
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <RocketLaunch />
            Whatzneu
          </Typography>
          <Button
            variant="contained"
            startIcon={<AdminPanelSettings />}
            onClick={() => navigate('/login')}
            sx={{
              background: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
              },
            }}
          >
            Admin Login
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box
          sx={{
            pt: { xs: 12, md: 16 },
            pb: { xs: 6, md: 10 },
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {/* Brand Logo/Name */}
          <Box
            sx={{
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <RocketLaunch sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 0.5,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Whatzneu
              </Typography>
              <Chip
                label="New Age Financing for SME Growth"
                sx={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  height: 32,
                }}
              />
            </Box>
          </Box>

          {/* Main Headline */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 900,
              mb: 3,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              lineHeight: 1.2,
              background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 50%, #fff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              backgroundSize: '200% auto',
              animation: 'textShine 3s linear infinite',
              '@keyframes textShine': {
                '0%': { backgroundPosition: '0% center' },
                '100%': { backgroundPosition: '200% center' },
              },
            }}
          >
            New Age Financing
            <br />
            Focused on SME Growth
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              color: alpha(theme.palette.common.white, 0.95),
              fontWeight: 400,
              maxWidth: '800px',
              mx: 'auto',
              fontSize: { xs: '1.1rem', md: '1.5rem' },
              textShadow: '0 2px 10px rgba(0,0,0,0.2)',
            }}
          >
            Transform your business with AI-driven financing solutions.
            <br />
            Start with <strong>₹1 Lakh</strong> and grow up to <strong>₹10 Lakhs</strong> based on your payment history.
          </Typography>

          {/* CTA Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              justifyContent: 'center',
              mb: 6,
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketLaunch />}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Apply Now
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Dashboard />}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Explore Dashboard
            </Button>
          </Stack>

          {/* Stats */}
          <Grid container spacing={3} sx={{ mt: 4, mb: 8 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card
                  sx={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      background: 'rgba(255, 255, 255, 0.25)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: 'white',
                        mb: 1,
                        fontSize: { xs: '1.8rem', md: '2.5rem' },
                      }}
                    >
                      {stat.value}
                      <Typography component="span" variant="h6" sx={{ ml: 0.5 }}>
                        {stat.suffix}
                      </Typography>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.9),
                        fontWeight: 500,
                        fontSize: { xs: '0.85rem', md: '1rem' },
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<AutoAwesome />}
              label="Technology First"
              sx={{
                mb: 2,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                height: 40,
                px: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Built for the Digital Age
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Every feature designed with cutting-edge technology specifically for SMEs,
              giving your business the competitive edge it deserves to grow and thrive.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${feature.color}, ${alpha(feature.color, 0.5)})`,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      background: 'rgba(255, 255, 255, 0.25)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                      '&::before': {
                        transform: 'scaleX(1)',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        color: feature.color,
                        mb: 2,
                        display: 'inline-flex',
                        p: 1.5,
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: 'white',
                        mb: 1.5,
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.85),
                        lineHeight: 1.7,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Use Cases Section */}
        <Box sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<Business />}
              label="Real Business Solutions"
              sx={{
                mb: 2,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                height: 40,
                px: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Solutions for Real Business Challenges
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              We understand the unique cash flow challenges SMEs face. Our short-term
              loans start from <strong>₹1 Lakh</strong> and can grow up to <strong>₹10 Lakhs</strong> based on
              your payment history. Build trust and unlock higher limits with consistent, timely repayments.
            </Typography>
          </Box>

          {/* Featured Use Cases */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {useCases
              .filter((uc) => uc.featured)
              .map((useCase, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.4)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        background: `linear-gradient(90deg, ${useCase.color}, ${alpha(useCase.color, 0.5)})`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-10px) scale(1.02)',
                        background: 'rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 16px 50px rgba(0, 0, 0, 0.3)',
                        border: `2px solid ${useCase.color}`,
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 3,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            color: useCase.color,
                            display: 'inline-flex',
                            p: 2,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.3)',
                            boxShadow: `0 4px 15px ${alpha(useCase.color, 0.3)}`,
                            flexShrink: 0,
                          }}
                        >
                          {useCase.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Chip
                            label="Featured"
                            size="small"
                            sx={{
                              mb: 1.5,
                              background: `linear-gradient(135deg, ${useCase.color}, ${alpha(useCase.color, 0.7)})`,
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              height: 24,
                            }}
                          />
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 800,
                              color: 'white',
                              mb: 1.5,
                              fontSize: { xs: '1.3rem', md: '1.5rem' },
                            }}
                          >
                            {useCase.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: alpha(theme.palette.common.white, 0.95),
                              lineHeight: 1.8,
                              fontSize: '1rem',
                            }}
                          >
                            {useCase.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          {/* Other Use Cases Grid */}
          <Grid container spacing={3}>
            {useCases
              .filter((uc) => !uc.featured)
              .map((useCase, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${useCase.color}, ${alpha(useCase.color, 0.5)})`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        background: 'rgba(255, 255, 255, 0.25)',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                        border: `1px solid ${alpha(useCase.color, 0.5)}`,
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          color: useCase.color,
                          mb: 2,
                          display: 'inline-flex',
                          p: 1.5,
                          borderRadius: 2,
                          background: 'rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        {useCase.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'white',
                          mb: 1.5,
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                        }}
                      >
                        {useCase.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: alpha(theme.palette.common.white, 0.85),
                          lineHeight: 1.7,
                        }}
                      >
                        {useCase.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>

        {/* Brand Story Section */}
        <Box
          sx={{
            py: 8,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            px: { xs: 3, md: 6 },
            mb: 8,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
              }}
            >
              Why Choose Whatzneu?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                mb: 3,
                lineHeight: 1.8,
                fontSize: '1.1rem',
              }}
            >
              Whatzneu is the new age financing company focused exclusively on SME growth.
              We're not just another lending platform - we're your growth partner. Our technology-first
              approach ensures small businesses get enterprise-level financial tools designed specifically
              for their unique needs. Our AI-driven platform processes applications in record time while
              maintaining the highest standards of security and transparency.
            </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha(theme.palette.common.white, 0.9),
                  mb: 3,
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                }}
              >
                Starting from <strong>₹1 Lakh</strong> with the potential to grow up to <strong>₹10 Lakhs</strong>
                based on your payment history, we understand the unique needs of growing businesses.
                Build your credit with us and unlock higher limits through consistent, timely payments.
                Our cloud-native infrastructure ensures 99.9% uptime, so you're never left waiting when you need funds most.
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
                <Chip
                  icon={<Business />}
                  label="SME Focused"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={<AccountBalance />}
                  label="1-10 Lakh Range"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={<TrendingUp />}
                  label="Payment History Based"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={<TrendingUp />}
                  label="Growth Partners"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                {[
                  { icon: <CloudSync />, text: '100% Cloud-Based Infrastructure' },
                  { icon: <Security />, text: 'Enterprise Security Standards' },
                  { icon: <Speed />, text: 'Real-Time Application Processing' },
                  { icon: <Analytics />, text: 'Advanced Business Analytics' },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        transform: 'translateX(10px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.05rem',
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Footer CTA */}
        <Box
          sx={{
            py: 6,
            textAlign: 'center',
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: 'white',
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Ready to Transform Your Business?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              mb: 4,
              fontWeight: 400,
            }}
          >
            Join hundreds of growing SMEs powered by Whatzneu
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<RocketLaunch />}
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.2rem',
              fontWeight: 700,
              background: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              color: 'white',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.35)',
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Get Started Today
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HomeScreen;
